import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { getSpaceGallery, uploadGalleryImage, deleteGalleryImage, getSpaceDocuments, uploadDocument, deleteDocument, formatFileSize, getFileIcon, canPreviewFile } from '../../services/spaceMediaService';
import { Close, Download, Delete, Visibility } from '@mui/icons-material';
import VisorDocumento from '../documentViewer/VisorDocumento';
import { urlDeArchivo } from '../../config';
import Lightbox from '../lightbox/Lightbox';
import { puedeAdministrarEspacio } from '../../utils/permisosEspacio';
import './SpaceMediaSidebar.scss';

/**
 * Repositorio de fotos/videos y documentos de un espacio.
 * Visible para cualquier persona autenticada; solo un administrador o el
 * encargado de este espacio puede añadir o eliminar contenido (el backend
 * también lo exige, esto solo adapta la interfaz para no mostrar acciones
 * que serían rechazadas).
 *
 * `mobileOpen` / `onCloseMobile` permiten controlarlo como panel deslizante
 * en pantallas pequeñas (ver Home.jsx).
 */
const SpaceMediaSidebar = ({ spaceId, spaceName, mobileOpen = false, onCloseMobile }) => {
  const { currentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('gallery');
  const [gallery, setGallery] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  // Posición de la foto abierta en el visor a pantalla completa, o null.
  // Se guarda el índice y no el objeto para poder navegar con las flechas.
  const [indiceVisor, setIndiceVisor] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadFileName, setUploadFileName] = useState('');
  // Documento abierto en el visor a pantalla completa, o null.
  const [documentoEnVisor, setDocumentoEnVisor] = useState(null);

  // Administrador, o encargado de ESTE espacio.
  const isAdmin = puedeAdministrarEspacio(currentUser, spaceId);

  useEffect(() => {
    fetchMedia();
  }, [spaceId]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const galleryData = await getSpaceGallery(spaceId);
      const docsData = await getSpaceDocuments(spaceId);
      setGallery(galleryData || []);
      setDocuments(docsData || []);
    } catch (err) {
      console.error('Error al cargar media:', err);
    }
    setLoading(false);
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isAdmin) {
      alert('Solo un administrador o el encargado del espacio puede agregar contenido a la galería');
      e.target.value = '';
      return;
    }

    setUploadFileName(file.name);
    setUploadProgress(0);
    setUploadError(null);

    try {
      setUploadingGallery(true);
      await uploadGalleryImage(
        spaceId,
        file,
        file.name.split('.')[0],
        '',
        currentUser?.id,
        (progress) => setUploadProgress(progress)
      );
      await fetchMedia();
      e.target.value = '';
      setUploadProgress(0);
      setUploadFileName('');
    } catch (error) {
      setUploadError(error.message);
      console.error('Error al subir:', error);
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isAdmin) {
      alert('Solo un administrador o el encargado del espacio puede subir documentos');
      e.target.value = '';
      return;
    }

    setUploadFileName(file.name);
    setUploadProgress(0);
    setUploadError(null);

    try {
      setUploadingDoc(true);
      await uploadDocument(
        spaceId,
        file,
        '',
        currentUser?.id,
        'general',
        (progress) => setUploadProgress(progress)
      );
      await fetchMedia();
      e.target.value = '';
      setUploadProgress(0);
      setUploadFileName('');
    } catch (error) {
      setUploadError(error.message);
      console.error('Error al subir:', error);
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteImage = async (id) => {
    if (!isAdmin) {
      alert('Solo un administrador o el encargado del espacio puede eliminar contenido de la galería');
      return;
    }
    if (!window.confirm('¿Eliminar esta foto?')) return;

    try {
      await deleteGalleryImage(id);
      setIndiceVisor(null);
      await fetchMedia();
    } catch (error) {
      alert('Error al eliminar: ' + error.message);
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!isAdmin) {
      alert('No tienes permisos para eliminar este documento');
      return;
    }
    if (!window.confirm('¿Eliminar este documento?')) return;

    try {
      await deleteDocument(id);
      await fetchMedia();
    } catch (error) {
      alert('Error al eliminar: ' + error.message);
    }
  };

  const handleDownload = (fileUrl, fileName) => {
    try {
      const link = document.createElement('a');
      // urlDeArchivo es imprescindible: en la base de datos la ruta queda
      // relativa ("/assets/documents/x.pdf") y los archivos los sirve el
      // BACKEND, no el servidor del frontend. Sin esta conversión el enlace
      // apuntaba al dominio equivocado y la descarga daba 404 en producción.
      link.href = urlDeArchivo(fileUrl);
      link.download = fileName;
      // El atributo `download` lo ignora el navegador cuando el archivo viene
      // de otro origen, así que se abre en una pestaña nueva en vez de
      // reemplazar la página y sacar a la persona de la plataforma.
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error al descargar:', err);
    }
  };

  const isVideoFile = (item) => {
    const name = (item?.fileName || '').toLowerCase();
    const url = (item?.imageUrl || '').toLowerCase();
    return (
      name.match(/\.(mp4|mov|avi|webm|mkv|3gp)$/) ||
      url.startsWith('data:video') ||
      url.match(/\.(mp4|mov|avi|webm|mkv|3gp)(\?|$)/)
    );
  };

  return (
    <div className={`space-media-sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="media-header">
        <span>Galería y Documentos</span>
        {onCloseMobile && (
          <button className="mobile-close-btn" onClick={onCloseMobile} aria-label="Cerrar">
            <Close fontSize="small" />
          </button>
        )}
      </div>

      {!isAdmin && (
        <div className="viewer-only-notice">
          Solo lectura — este contenido lo gestiona el encargado del espacio
        </div>
      )}

      <div className="media-tabs">
        <button
          className={`tab-btn ${activeTab === 'gallery' ? 'active' : ''}`}
          onClick={() => setActiveTab('gallery')}
        >
          Galería
        </button>
        <button
          className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          Documentos
        </button>
      </div>

      <div className="media-content">
        {activeTab === 'gallery' && (
          <div className="gallery-section">
            {isAdmin && (
              <div className="upload-zone">
                <label className="upload-label">
                  <span>Subir foto o video</span>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleUploadImage}
                    disabled={uploadingGallery}
                  />
                </label>
              </div>
            )}

            {loading && <p className="loading">Cargando...</p>}

            {gallery.length === 0 && !loading && (
              <p className="empty-state">Sin fotos aún</p>
            )}

            <div className="gallery-grid">
              {gallery.map((img) => (
                <div key={img.id} className="gallery-item">
                  {isVideoFile(img) ? (
                    <video
                      src={urlDeArchivo(img.imageUrl)}
                      className="gallery-video"
                      muted
                      preload="metadata"
                      onClick={() => setIndiceVisor(gallery.indexOf(img))}
                    />
                  ) : (
                    <img
                      src={urlDeArchivo(img.imageUrl)}
                      alt={img.title}
                      onClick={() => setIndiceVisor(gallery.indexOf(img))}
                    />
                  )}
                  {isAdmin && (
                    <button
                      className="delete-btn-corner"
                      onClick={() => handleDeleteImage(img.id)}
                      title="Eliminar foto"
                      aria-label="Eliminar foto"
                    >
                      <Delete fontSize="small" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="documents-section">
            {isAdmin && (
              <div className="upload-zone">
                <label className="upload-label">
                  <span>Subir documento</span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    onChange={handleUploadDocument}
                    disabled={uploadingDoc}
                  />
                </label>
              </div>
            )}

            {loading && <p className="loading">Cargando...</p>}

            {documents.length === 0 && !loading && (
              <p className="empty-state">Sin documentos aún</p>
            )}

            <div className="documents-list">
              {documents.map((doc) => (
                <div key={doc.id} className="document-item">
                  <div className="doc-icon">{getFileIcon(doc.fileType, doc.fileName)}</div>
                  <div
                    className={`doc-info ${canPreviewFile(doc.fileType, doc.fileName) ? 'clickable' : ''}`}
                    onClick={() => canPreviewFile(doc.fileType, doc.fileName) && setDocumentoEnVisor(doc)}
                  >
                    <p className="doc-name">{doc.fileName}</p>
                    <p className="doc-meta">{formatFileSize(doc.fileSize)}</p>
                  </div>
                  <div className="doc-actions">
                    {/* Los PDF, las imágenes y el texto plano se leen aquí
                        mismo; no hace falta descargarlos para verlos. */}
                    {canPreviewFile(doc.fileType, doc.fileName) && (
                      <button
                        className="preview-btn"
                        onClick={() => setDocumentoEnVisor(doc)}
                        title="Ver documento"
                      >
                        <Visibility />
                      </button>
                    )}
                    <button
                      className="download-btn"
                      onClick={() => handleDownload(doc.fileUrl, doc.fileName)}
                      title="Descargar"
                    >
                      <Download />
                    </button>
                    {isAdmin && (
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteDocument(doc.id)}
                        title="Eliminar"
                      >
                        <Delete />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Visor a pantalla completa: sustituye al recuadro anterior, que
          dejaba la página de fondo visible y mostraba la foto más pequeña
          que cualquier visor del sistema. */}
      {indiceVisor !== null && (
        <Lightbox
          items={gallery.map((g) => ({
            url: g.imageUrl,
            titulo: g.title,
            descripcion: g.description,
          }))}
          indice={indiceVisor}
          onIndice={setIndiceVisor}
          onCerrar={() => setIndiceVisor(null)}
        />
      )}

      {/* Visor de documentos a pantalla completa */}
      {documentoEnVisor && (
        <VisorDocumento
          documento={documentoEnVisor}
          onCerrar={() => setDocumentoEnVisor(null)}
          onDescargar={(doc) => handleDownload(doc.fileUrl, doc.fileName)}
        />
      )}

      {/* UPLOAD PROGRESS MODAL */}
      {(uploadingGallery || uploadingDoc) && (
        <div className="upload-modal">
          <div className="upload-content">
            <div className="upload-spinner"></div>
            <h3>Subiendo archivo...</h3>
            <p className="upload-filename">{uploadFileName}</p>

            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <span className="progress-text">{uploadProgress}%</span>
            </div>

            <p className="upload-status">
              {uploadProgress < 50
                ? 'Leyendo archivo...'
                : 'Enviando al servidor...'}
            </p>
          </div>
        </div>
      )}

      {/* ERROR MODAL */}
      {uploadError && (
        <div className="error-modal" onClick={() => setUploadError(null)}>
          <div className="error-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="error-close"
              onClick={() => setUploadError(null)}
            >
              ✕
            </button>
            <h3>Error al subir</h3>
            <p>{uploadError}</p>
            <button
              className="error-btn"
              onClick={() => setUploadError(null)}
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpaceMediaSidebar;
